import moment from "moment";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import PlayerAvatar from "@/components/PlayerAvatar";
import {
	DsEmpty,
	DsHeader,
	DsKpi,
	DsKpiRow,
	DsMissingSources,
	DsPreviewBadge,
	DsPreviewNotice,
	DsSection,
	DsSkeleton,
	DsStatus,
	DsTable,
} from "@/components/DsKit";

import {useClubAgents, useClubSnapshot} from "@/utils/hooks";
import {
	annualizedDsSalary,
	dsMembershipLabel,
	formatEuro,
	playerName,
	summarizeDsContractCosts,
} from "@/utils/clubSnapshot";
import {demoAgency} from "@/utils/clubDemo";

const COMMISSION_STATUS = {
	paid: {status: "ok", label: "Pagata"},
	due: {status: "warn", label: "Da pagare"},
	overdue: {status: "alert", label: "Scaduta"},
};

function DsAgentsPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({
		handleNotification,
	});
	const {data: agentsData, loading: agentsLoading} = useClubAgents({
		snapshot: s,
	});

	const agency = s && agentsData ? demoAgency(s, agentsData) : null;
	const inScope = (playerId) => !!s?.playerById.has(playerId);
	const agents = (agency?.agents || [])
		.map((a) => ({...a, clients: a.clients.filter((c) => inScope(c.playerId))}))
		.filter((a) => a.clients.length);
	const commissions = (agency?.commissions || []).filter((c) =>
		inScope(c.playerId),
	);
	const assisted = new Set(
		agents.flatMap((a) => a.clients.map((c) => c.playerId)),
	);
	const in60 = moment().add(60, "days");
	const dueSoon = commissions.filter(
		(c) => c.status !== "paid" && moment(c.dueDate).isBefore(in60),
	);
	const sum = (rows) => rows.reduce((n, c) => n + c.amount, 0);

	const ready = s && !agentsLoading;

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Procuratori"
					subtitle="Rapporti con gli agenti, assistiti e commissioni"
					snapshot={s}
					onReload={reload}
					reloading={loading}
					scope={scope}
					area="mercato"
				/>
				<DsSkeleton visible={!ready} />
				{ready && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsPreviewNotice>
							{agency?.agentsPreview
								? "Nessun procuratore inserito nelle schede giocatore: procuratori, mandati e commissioni sono d'esempio, calcolati sui contratti reali."
								: "I procuratori arrivano dallo storico agenti delle schede giocatore; mandati e commissioni sono d'esempio, calcolati sui contratti reali."}
						</DsPreviewNotice>

						<DsKpiRow>
							<DsKpi label="Procuratori" value={agents.length} />
							<DsKpi
								label="Giocatori assistiti"
								value={`${assisted.size}/${s.totalPlayers}`}
								sub={`${s.totalPlayers - assisted.size} senza procuratore`}
							/>
							<DsKpi
								label="Commissioni stagione"
								value={formatEuro(sum(commissions))}
								sub={`${formatEuro(sum(commissions.filter((c) => c.status === "paid")))} già pagate`}
							/>
							<DsKpi
								label="Da pagare in 60 giorni"
								value={formatEuro(sum(dueSoon))}
								sub={`${dueSoon.length} pagamenti`}
								tone={
									dueSoon.some((c) => c.status === "overdue")
										? "red"
										: dueSoon.length
											? "yellow"
											: undefined
								}
							/>
						</DsKpiRow>

						<DsSection
							title="Procuratori e assistiti"
							hint="Per ogni agente: chi rappresenta, quanto valgono i contratti e cosa scade"
						>
							{agents.length ? (
								<div className="ds-org-grid">
									{agents.map((a) => {
										const contracts = a.clients.flatMap(
											(c) => s.contractsByPlayer.get(c.playerId) || [],
										);
										const annual = annualizedDsSalary(
											summarizeDsContractCosts(contracts).monthlySalary,
										);
										const expiring = contracts.filter(
											(c) => c.daysLeft !== null && c.daysLeft <= 365,
										);
										const agentCommissions = commissions.filter(
											(c) => c.agent.id === a.id,
										);
										const mandateDays = moment(a.mandateExpiry).diff(
											moment(),
											"days",
										);
										return (
											<div key={a.id} className="ds-org ds-org--ok">
												<div className="ds-org__head">
													<div className="ds-org__title">
														<span className="ds-org__name">
															{a.name} {a.preview && <DsPreviewBadge />}
														</span>
														<span className="ds-org__count">
															{a.clients.length}{" "}
															{a.clients.length === 1
																? "assistito"
																: "assistiti"}{" "}
															·{" "}
															{contracts.length
																? `contratti ${formatEuro(annual)} annui`
																: "nessun contratto"}
														</span>
													</div>
													<div className="ds-org__coverage">
														<span className="ds-org__coverage-value">
															{formatEuro(
																agentCommissions.reduce(
																	(n, c) => n + c.amount,
																	0,
																),
															)}
														</span>
														<span className="ds-org__coverage-label">
															commissioni
														</span>
													</div>
												</div>
												<div className="ds-org__roles">
													<span
														className={
															"ds-org__role" +
															(expiring.length ? " is-missing" : " is-ok")
														}
													>
														{expiring.length
															? `${expiring.length} contratti in scadenza entro un anno`
															: "Nessun contratto in scadenza"}
													</span>
													<span
														className={
															"ds-org__role" +
															(mandateDays <= 90 ? " is-missing" : " is-ok")
														}
														title="Dato d'esempio"
													>
														Mandato fino a{" "}
														{moment(a.mandateExpiry).format("MMM YYYY")}
													</span>
												</div>
												<div className="ds-org__groups">
													<div className="ds-org__group">
														{a.clients.map((c) => {
															const player = s.playerById.get(c.playerId);
															const own =
																s.contractsByPlayer.get(c.playerId) || [];
															return (
																<div
																	key={c.playerId}
																	className="ds-org__member"
																	onClick={() =>
																		navigate("/ds/giocatore/" + c.playerId)
																	}
																>
																	<PlayerAvatar
																		firstName={player?.firstName}
																		lastName={player?.lastName}
																		imageUrl={player?.imageUrl}
																		size="small"
																	/>
																	<span className="ds-org__member-body">
																		<span className="ds-org__member-name">
																			{playerName(player)}
																		</span>
																		<span className="ds-org__member-role">
																			{[
																				dsMembershipLabel({
																					teams: s.teamsByPlayer.get(
																						c.playerId,
																					),
																				}),
																				own.length === 1 && own[0].endDate
																					? "contratto fino a " +
																						moment(own[0].endDate).format(
																							"MMM YYYY",
																						)
																					: own.length > 1
																						? `${own.length} contratti`
																						: "senza contratto",
																			]
																				.filter(Boolean)
																				.join(" · ")}
																		</span>
																	</span>
																</div>
															);
														})}
													</div>
												</div>
											</div>
										);
									})}
								</div>
							) : (
								<DsEmpty>Nessun procuratore per la selezione.</DsEmpty>
							)}
						</DsSection>

						<DsSection
							title="Commissioni"
							preview
							hint="Su acquisto (percentuale del cartellino) e sul contratto dell'assistito (percentuale del lordo annuo)"
						>
							<DsTable
								columns={[
									{
										key: "agent",
										label: "Procuratore",
										render: (c) => <b>{c.agent.name}</b>,
									},
									{
										key: "player",
										label: "Giocatore",
										render: (c) => (
											<>
												<div>{playerName(c.player)}</div>
												<div className="ds-muted">{dsMembershipLabel(c)}</div>
											</>
										),
									},
									{
										key: "operation",
										label: "Su",
										render: (c) =>
											`${c.operation === "Acquisto" ? "Acquisto" : "Contratto"}${c.tranche ? " · rata " + c.tranche : ""}`,
									},
									{
										key: "base",
										label: "Base",
										align: "right",
										render: (c) =>
											`${formatEuro(c.base)} · ${Math.round(c.rate * 100)}%`,
									},
									{
										key: "amount",
										label: "Importo",
										align: "right",
										render: (c) => <b>{formatEuro(c.amount)}</b>,
									},
									{
										key: "due",
										label: "Scadenza",
										render: (c) => moment(c.dueDate).format("D MMM YYYY"),
									},
									{
										key: "status",
										label: "Stato",
										render: (c) => (
											<DsStatus
												status={COMMISSION_STATUS[c.status].status}
												label={COMMISSION_STATUS[c.status].label}
											/>
										),
									},
								]}
								rows={[...commissions].sort(
									(a, b) => moment(a.dueDate) - moment(b.dueDate),
								)}
								onRowClick={(c) => navigate("/ds/giocatore/" + c.playerId)}
								empty="Nessuna commissione per la selezione."
							/>
						</DsSection>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsAgentsPage;
